import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyLeagues.css';

/* The list of competitions this company runs, and the form that starts one.

   A league is days that are NOT next to each other, held together by a standing — an office
   olympics, a season, a year of quizzes. Structurally it is a master event with the days hanging
   under it, so every day is an ordinary event with its own registration, its own teams and its
   own leaderboard.

   ★★ The set-up asks ONE question that changes what the league is, and it is a question about
   where the teams live:

     the same teams all season   they hang on the master event, so one team plays every day and
                                 the standing is a TEAM standing.
     new teams every day         they hang on each day, so people end up in different teams and a
                                 team standing would be meaningless — the standing is per PERSON.

   That is not a preference between two implementations: the second half of each line follows
   from the first. Which is why the form says both halves out loud.

   Everything else has a defensible default and lives on the league's own Set-up tab. */

const guessZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Brussels';
  } catch {
    return 'Europe/Brussels';
  }
};

const emptyDraft = () => ({
  name: '', date: '', end_date: '', short_desc: '',
  team_scope: 'master',
  scoring_scheme: '', participation_points: 0, count_best_of: '',
  standings_are_public: true, time_zone: guessZone(),
});

export const SCOPES = ['master', 'event'];

/* ★ The server's own sentence, when it sent one.

   ``apiErrorText`` renders the error KEY ("cannotSave"), which is right for the generic
   failures every screen shares. The league service deliberately raises messages written to be
   read by the person who caused them — "A day has already been scored. Re-open every day before
   changing who competes" — and collapsing that into "Could not save" throws away the only part
   that says what to do instead. So: the message if there is one, the translated key otherwise. */
export const leagueError = (t, err) => {
  const message = err && err.data && err.data.message;
  return (typeof message === 'string' && message.trim()) ? message : apiErrorText(t, err);
};

const CompanyLeagues = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/company/leagues')
      .then((res) => {
        setLeagues(res.leagues || []);
        setTournaments(res.tournaments || []);
      })
      // Not a contact person, or no engagement product — either way this screen is not for you.
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const create = () => {
    if (busy || !draft || !draft.name.trim() || !draft.date) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/company/leagues', {
      ...draft,
      // A blank "best of" means every day counts; the API reads null, not ''.
      count_best_of: draft.count_best_of === '' ? null : Number(draft.count_best_of),
    })
      .then((res) => {
        setDraft(null);
        navigate(`/league/${res.league.id}`);
      })
      .catch((err) => setNotice(leagueError(t, err)))
      .finally(() => setBusy(false));
  };

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setDraft((d) => ({ ...d, [key]: value }));
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="leaderboard" theme="primary" className="me-2" />
          {t('leaguesAndTournaments')}
        </Title>

        {leagues === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('leaguesNote')}</p>

            {leagues.length === 0 && tournaments.length === 0 && !draft && (
              <p className="ce-note">{t('noLeaguesYet')}</p>
            )}

            {leagues.map((lg) => (
              <button
                key={lg.id}
                type="button"
                className={`ce-row ${lg.is_closed ? 'is-past' : ''}`}
                onClick={() => navigate(`/league/${lg.id}`)}
              >
                <span className="lg-badge">
                  <Icon name={lg.is_closed ? 'trophy' : 'leaderboard'} />
                </span>
                <span className="ce-row__body">
                  <span className="ce-row__top">
                    <span className="ce-row__name">{lg.name}</span>
                    {lg.is_closed && (
                      <span className="ce-pill ce-pill--going">{t('leagueFinished')}</span>
                    )}
                  </span>
                  <span className="ce-row__meta">
                    {t(`leagueSubject_${lg.subject_type}`)}
                    {' · '}
                    {t('leagueDayProgress', { closed: lg.closed_count, total: lg.day_count })}
                    {lg.competitor_count
                      ? ` · ${t('leagueCompetitorCount', { count: lg.competitor_count })}` : ''}
                  </span>
                  {/* The leader is the one thing a list row can say that a count cannot:
                      "12 days" answers how big, never who is winning. */}
                  {lg.leader && (
                    <span className="ce-row__meta lg-leader">
                      <Icon name="crown" className="me-1" />
                      {lg.leader}
                    </span>
                  )}
                </span>
                <span className="ce-row__actions"><Icon name="angle-small-right" /></span>
              </button>
            ))}

            {/* ★ The single-day competitions, under the seasons. They are READ here and set up
                on the day itself — a competition hangs on an event, so the event comes first,
                which is also why there is no "new tournament" button beside "new league". The
                row leads straight to that setup rather than to the event: somebody who opened
                this screen came for the competition, not for the venue and the invite list. */}
            {tournaments.length > 0 && (
              <>
                <p className="bravos-form__label mt-3">{t('oneDayCompetitions')}</p>
                {tournaments.map((tn) => (
                  <button
                    key={tn.id}
                    type="button"
                    className={`ce-row ${tn.is_open ? '' : 'is-past'}`}
                    onClick={() => navigate(`/company-event/${tn.id}/tournament`)}
                  >
                    <span className="lg-badge">
                      <Icon name={tn.is_open ? 'flag-checkered' : 'trophy'} />
                    </span>
                    <span className="ce-row__body">
                      <span className="ce-row__top">
                        <span className="ce-row__name">{tn.name}</span>
                        {!tn.is_open && (
                          <span className="ce-pill ce-pill--going">{t('leagueFinished')}</span>
                        )}
                      </span>
                      <span className="ce-row__meta">
                        {shortDate(tn.date, i18n.language)}
                        {' · '}
                        {t(`tournamentMode_${tn.mode}`)}
                        {' · '}
                        {t(`tournamentFormat_${tn.format}`)}
                      </span>
                    </span>
                    <span className="ce-row__actions"><Icon name="angle-small-right" /></span>
                  </button>
                ))}
              </>
            )}

            {!draft && (
              <button
                type="button"
                className="bravos-member__makeadmin mt-3"
                onClick={() => setDraft(emptyDraft())}
              >
                <Icon name="plus" className="me-1" />{t('newLeague')}
              </button>
            )}

            {draft && (
              <div className="bravos-form mt-3">
                <label>{t('leagueName')}</label>
                <input value={draft.name} onChange={set('name')} maxLength={50} />

                <div className="lg-two">
                  <span>
                    <label>{t('leagueStart')}</label>
                    <input type="date" value={draft.date} onChange={set('date')} />
                  </span>
                  <span>
                    <label>{t('leagueEnd')}</label>
                    <input type="date" value={draft.end_date} onChange={set('end_date')} />
                  </span>
                </div>

                {/* ★ The question the whole model turns on. Both options spell out their
                    consequence, because the consequence is not a detail — it decides whether a
                    team standing is possible at all. */}
                <label>{t('leagueTeamsQuestion')}</label>
                <select value={draft.team_scope} onChange={set('team_scope')}>
                  {SCOPES.map((s) => (
                    <option key={s} value={s}>{t(`leagueScope_${s}`)}</option>
                  ))}
                </select>
                <p className="ce-check__hint">{t(`leagueScope_${draft.team_scope}_hint`)}</p>

                <label>{t('leagueLadder')}</label>
                <input
                  value={draft.scoring_scheme}
                  onChange={set('scoring_scheme')}
                  placeholder="25,18,15,12,10,8,6,4,2,1"
                  maxLength={200}
                />
                <p className="ce-check__hint">{t('leagueLadderHint')}</p>

                <div className="lg-two">
                  <span>
                    <label>{t('leagueParticipation')}</label>
                    <input
                      type="number" min="0"
                      value={draft.participation_points}
                      onChange={set('participation_points')}
                    />
                  </span>
                  <span>
                    <label>{t('leagueBestOf')}</label>
                    <input
                      type="number" min="1"
                      value={draft.count_best_of}
                      onChange={set('count_best_of')}
                      placeholder={t('leagueBestOfAll')}
                    />
                  </span>
                </div>
                <p className="ce-check__hint">{t('leagueParticipationHint')}</p>

                <label className="ce-check">
                  <input
                    type="checkbox"
                    checked={draft.standings_are_public}
                    onChange={set('standings_are_public')}
                  />
                  <span>{t('leaguePublicStandings')}</span>
                </label>

                {notice && <p className="ce-note ce-note--warn">{notice}</p>}

                <div className="ce-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy || !draft.name.trim() || !draft.date}
                    onClick={create}
                  >
                    {t('leagueCreate')}
                  </button>
                  <button
                    type="button"
                    className="bravos-member__makeadmin"
                    onClick={() => { setDraft(null); setNotice(null); }}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </Base>
  );
};

export default CompanyLeagues;
